import { supabase } from '@/integrations/supabase/client';
import { RecurringSubscription } from '@/types/database/recurring-subscriptions';

/**
 * Check if subscription bot is configured
 */
export const isSubscriptionBotConfigured = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.functions.invoke('telegram-subscription-bot', {
      body: { action: 'test-connection' }
    });
    return data?.success === true;
  } catch {
    return false;
  }
};

/**
 * Get subscription bot connection for a user
 */
export const getUserSubscriptionBotConnection = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('telegram_connections')
      .select('*')
      .eq('user_id', userId)
      .eq('bot_type', 'subscription')
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      console.error('Error getting subscription bot connection:', error);
      return null;
    }
    return data;
  } catch (error) {
    console.error('Error getting subscription bot connection:', error);
    return null;
  }
};

/**
 * Generate connection code for subscription bot
 */
export const generateSubscriptionBotConnectionCode = async (userId: string): Promise<{ code: string; expires_at: string } | null> => {
  try {
    const code = Math.random().toString(36).substring(2, 10).toUpperCase();
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 24);

    // Delete existing unused codes
    await supabase
      .from('telegram_connection_codes')
      .delete()
      .eq('user_id', userId)
      .eq('bot_type', 'subscription')
      .eq('used', false);

    const { error } = await supabase
      .from('telegram_connection_codes')
      .insert({
        user_id: userId,
        code,
        expires_at: expiresAt.toISOString(),
        bot_type: 'subscription'
      });

    if (error) {
      console.error('Error generating connection code:', error);
      return null;
    }

    return { code, expires_at: expiresAt.toISOString() };
  } catch (error) {
    console.error('Failed to generate connection code:', error);
    return null;
  }
};

/**
 * Disconnect user from subscription bot
 */
export const disconnectSubscriptionBotUser = async (userId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('telegram_connections')
      .update({ is_active: false })
      .eq('user_id', userId)
      .eq('bot_type', 'subscription');

    return !error;
  } catch {
    return false;
  }
};

/**
 * Get all connected subscription bot chat IDs
 */
const getAllConnectedChatIds = async (): Promise<number[]> => {
  try {
    const { data } = await supabase
      .from('telegram_connections')
      .select('telegram_chat_id')
      .eq('bot_type', 'subscription')
      .eq('is_active', true);

    return data?.map(c => c.telegram_chat_id) || [];
  } catch {
    return [];
  }
};

/**
 * Send notification to all connected subscription bot users
 */
const sendToAllConnected = async (message: string): Promise<void> => {
  const chatIds = await getAllConnectedChatIds();
  if (chatIds.length === 0) return;

  try {
    await supabase.functions.invoke('telegram-subscription-bot', {
      body: { action: 'send-notification', message, chatIds }
    });
  } catch (error) {
    console.error('Error sending subscription notification:', error);
  }
};

/**
 * Notify when a new subscription is created
 */
export const notifySubscriptionCreated = async (sub: RecurringSubscription): Promise<void> => {
  const amount = Number(sub.amount).toLocaleString('en', { minimumFractionDigits: 2 });
  const msg =
    `📝 *New Subscription Created*\n\n` +
    `*${sub.name}*\n` +
    `💰 ${amount} ${sub.currency || 'ETB'} • ${sub.frequency}\n` +
    (sub.vendor_name ? `🏢 ${sub.vendor_name}\n` : '') +
    `📅 Next due: ${sub.next_due_date}\n` +
    (sub.category?.name ? `📂 ${sub.category.name}` : '');

  await sendToAllConnected(msg);
};

/**
 * Notify when a subscription is edited
 */
export const notifySubscriptionEdited = async (sub: RecurringSubscription): Promise<void> => {
  const amount = Number(sub.amount).toLocaleString('en', { minimumFractionDigits: 2 });
  const msg =
    `✏️ *Subscription Updated*\n\n` +
    `*${sub.name}*\n` +
    `💰 ${amount} ${sub.currency || 'ETB'} • ${sub.frequency}\n` +
    `📅 Next due: ${sub.next_due_date}`;

  await sendToAllConnected(msg);
};

/**
 * Notify when expenses are generated from subscriptions
 */
export const notifyExpensesGenerated = async (generated: Array<{ subscription_name: string; expense_number: string }>): Promise<void> => {
  if (generated.length === 0) return;

  let msg = `💰 *Expenses Generated from Subscriptions*\n\n`;
  for (const item of generated.slice(0, 15)) {
    msg += `• ${item.subscription_name} → ${item.expense_number}\n`;
  }
  if (generated.length > 15) {
    msg += `\n... and ${generated.length - 15} more`;
  }

  await sendToAllConnected(msg);
};